import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Loader2, ChevronRight, Filter, X, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const PLAN_BADGE = {
  solo:   { label: 'Solo',   color: 'bg-emerald-100 text-emerald-800' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-800' },
  multi:  { label: 'Multi',  color: 'bg-purple-100 text-purple-800' },
};
const STATUS_BADGE = {
  trialing: 'bg-amber-100 text-amber-800',
  active:   'bg-green-100 text-green-800',
  past_due: 'bg-red-100 text-red-800',
  canceled: 'bg-gray-200 text-gray-700',
};

export default function PlatformSubscribers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const nav = useNavigate();

  const fetchData = useCallback(async (currentPage = 1) => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: pageSize };
      if (q) params.q = q;
      if (planFilter) params.plan = planFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get('/platform/subscribers', { params });
      setRows(data.rows);
      setTotal(data.total);
      setHasMore(data.has_more);
      setPage(data.page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [q, planFilter, statusFilter, pageSize]);

  useEffect(() => { fetchData(1); }, [planFilter, statusFilter, fetchData]);
  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => fetchData(1), 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const clearFilters = () => { setQ(''); setPlanFilter(''); setStatusFilter(''); };
  const hasAnyFilter = q || planFilter || statusFilter;

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="platform-subscribers">
      <header className="flex items-center gap-3">
        <Users className="w-6 h-6 text-brand"/>
        <div>
          <h1 className="font-heading text-2xl font-bold">Subscribers</h1>
          <p className="text-sm text-fg-muted">All clinics on the platform — search, filter, drill in.</p>
        </div>
      </header>

      {/* Search + filter bar */}
      <div className="bento-card space-y-3" data-testid="subscriber-filters">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search clinic name, slug, or owner…"
                   className="w-full pl-9 pr-3 py-2 border border-soft rounded-md text-sm"
                   data-testid="subscriber-search"/>
          </div>
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                  className="border border-soft rounded-md px-3 py-2 text-sm"
                  data-testid="subscriber-plan-filter">
            <option value="">All plans</option>
            <option value="solo">Solo</option>
            <option value="medium">Medium</option>
            <option value="multi">Multi-clinic</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-soft rounded-md px-3 py-2 text-sm"
                  data-testid="subscriber-status-filter">
            <option value="">All statuses</option>
            <option value="trialing">Trialing</option>
            <option value="active">Active</option>
            <option value="past_due">Past due</option>
            <option value="canceled">Canceled</option>
          </select>
          {hasAnyFilter && (
            <button onClick={clearFilters}
                    className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
                    data-testid="subscriber-clear-filters">
              <X className="w-3 h-3"/> Clear
            </button>
          )}
        </div>
        <div className="text-xs text-fg-muted flex items-center gap-1.5">
          <Filter className="w-3 h-3"/>
          {loading ? 'Loading…' : `${total} subscriber${total === 1 ? '' : 's'} ${hasAnyFilter ? 'matched' : 'total'}`}
        </div>
      </div>

      {/* Table */}
      <div className="bento-card overflow-x-auto" data-testid="subscriber-table">
        <table className="w-full text-sm">
          <thead className="text-xs text-fg-muted uppercase tracking-wider border-b border-soft">
            <tr>
              <th className="text-left py-2 px-2">Clinic</th>
              <th className="text-left py-2 px-2">Owner</th>
              <th className="text-left py-2 px-2">Plan</th>
              <th className="text-left py-2 px-2">Status</th>
              <th className="text-right py-2 px-2">MAU&nbsp;30d</th>
              <th className="text-left py-2 px-2">Trial / renews</th>
              <th className="text-left py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline"/></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="py-8 text-center text-fg-muted">No clinics match these filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.clinic_id} onClick={() => nav(`/platform/subscribers/${r.clinic_id}`)}
                  className="border-b border-soft/50 hover:bg-bg-secondary/40 cursor-pointer"
                  data-testid={`subscriber-row-${r.clinic_id}`}>
                <td className="py-2.5 px-2">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-[11px] text-fg-muted">{r.slug || r.clinic_id}</div>
                </td>
                <td className="py-2.5 px-2">
                  {r.owner ? (
                    <>
                      <div>{r.owner.full_name || r.owner.name || '—'}</div>
                      <div className="text-[11px] text-fg-muted">{r.owner.email}</div>
                    </>
                  ) : <span className="text-fg-muted">(no owner)</span>}
                </td>
                <td className="py-2.5 px-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${PLAN_BADGE[r.plan]?.color || 'bg-gray-100'}`}
                        data-testid={`plan-${r.clinic_id}`}>
                    {PLAN_BADGE[r.plan]?.label || r.plan}
                  </span>
                  {r.comp_until && (r.comp_remaining_days ?? -1) >= 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800"
                          title={`Comp'd until ${r.comp_until}`}
                          data-testid={`comp-pill-${r.clinic_id}`}>
                      COMP
                    </span>
                  )}
                  {r.usage?.approaching_limit && (
                    <span className={`ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                      r.usage.over_limit ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                    }`}
                          title={`${Math.round(r.usage.max_pct * 100)}% of ${r.plan} plan — ${r.usage.seats_used}/${r.usage.seats_max} seats · ${Math.round(r.usage.storage_bytes/1024/1024)}MB / ${Math.round(r.usage.storage_max_bytes/1024/1024/1024)}GB storage`}
                          data-testid={`usage-pill-${r.clinic_id}`}>
                      <Bell className="w-2.5 h-2.5"/>{Math.round(r.usage.max_pct * 100)}%
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_BADGE[r.subscription_status] || 'bg-gray-100'}`}>
                    {r.subscription_status}
                  </span>
                </td>
                <td className="py-2.5 px-2 text-right tabular-nums">{r.mau_30d_pings}</td>
                <td className="py-2.5 px-2 text-[11px] text-fg-muted">
                  {r.subscription_status === 'trialing' && r.trial_remaining_days !== null
                    ? `Trial: ${r.trial_remaining_days}d left`
                    : r.stripe_current_period_end
                      ? `Renews ${new Date(r.stripe_current_period_end).toLocaleDateString()}`
                      : '—'}
                </td>
                <td className="py-2.5 px-2 text-right">
                  <ChevronRight className="w-4 h-4 text-fg-muted inline"/>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-fg-muted">Page {page} • {rows.length} of {total}</span>
          <div className="flex gap-2">
            <button onClick={() => fetchData(page - 1)} disabled={page <= 1 || loading}
                    className="px-3 py-1.5 border border-soft rounded-md disabled:opacity-40"
                    data-testid="subscriber-prev">Previous</button>
            <button onClick={() => fetchData(page + 1)} disabled={!hasMore || loading}
                    className="px-3 py-1.5 border border-soft rounded-md disabled:opacity-40"
                    data-testid="subscriber-next">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
